import { CircleXmarkRegular } from "@icons";
import { useTranslation } from "react-i18next";

import { ComparisonFilterValue } from "../menu/types";
import { TextOperator } from "../menu/types";
import {
  BooleanFilterEntry,
  ComparisonFilterEntry,
  FilterEntry,
  TextFilterEntry,
} from "../use-filter-state";
import ComparisonOperatorDropdown from "./comparison-operator-dropdown";
import {
  DATE_OPERATOR_LABEL_KEYS,
  NUMERIC_OPERATOR_LABEL_KEYS,
  TEXT_OPERATOR_LABEL_KEYS,
} from "./constants";
import TextOperatorDropdown from "./text-operator-dropdown";

export default function FilterChip({
  entry,
  onTextOperatorChange,
  onComparisonOperatorChange,
  onRemove,
}: {
  entry: FilterEntry;
  onTextOperatorChange: (name: string, operator: TextOperator) => void;
  onComparisonOperatorChange: (
    name: string,
    value: ComparisonFilterValue,
  ) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  if ("group" in entry) return null;

  const isBoolean = "type" in entry && entry.type === "BOOLEAN";
  const isText = "values" in entry;

  let operatorLabel: string;
  let valueLabel: string;

  if (isBoolean) {
    const boolEntry = entry as BooleanFilterEntry;
    operatorLabel = t("textOperatorIs");
    valueLabel = t(boolEntry.value === "1" ? "booleanYes" : "booleanNo");
  } else if (isText) {
    const textEntry = entry as TextFilterEntry;
    operatorLabel = t(TEXT_OPERATOR_LABEL_KEYS[textEntry.operator]);
    const optionsMap = new Map(
      (textEntry.options ?? []).map((o) => [o.value, o.label]),
    );
    valueLabel = textEntry.values
      .map((v) => {
        const label = optionsMap.get(v);
        return typeof label === "string" ? label : v;
      })
      .join(", ");
  } else {
    const compEntry = entry as ComparisonFilterEntry;
    const keys =
      compEntry.type === "DATE"
        ? DATE_OPERATOR_LABEL_KEYS
        : NUMERIC_OPERATOR_LABEL_KEYS;
    operatorLabel = t(keys[compEntry.operator]).toLowerCase();
    valueLabel = compEntry.value;
  }

  return (
    <div className="flex shrink-0 items-center gap-1 rounded-md border border-divider bg-page px-2 py-1 text-xs text-default">
      <span className="font-medium">{entry.label}</span>
      {isBoolean ? (
        <span className="text-muted">{operatorLabel}</span>
      ) : isText ? (
        <TextOperatorDropdown
          entry={entry as TextFilterEntry}
          operatorLabel={operatorLabel}
          onOperatorChange={onTextOperatorChange}
        />
      ) : (
        <ComparisonOperatorDropdown
          entry={entry as ComparisonFilterEntry}
          operatorLabel={operatorLabel}
          onOperatorChange={onComparisonOperatorChange}
        />
      )}
      <span className="max-w-32 truncate">{valueLabel}</span>
      <button
        type="button"
        onClick={onRemove}
        className="relative hit-target-min ml-1 flex items-center text-muted hover:text-muted"
        data-target-gap="filter-remove"
        aria-label={
          typeof entry.label === "string"
            ? t("removeFilterNamed", {
                label: entry.label,
                interpolation: { escapeValue: false },
              })
            : t("removeFilter")
        }
      >
        <CircleXmarkRegular />
      </button>
    </div>
  );
}
